import './commitment-avatar.scss';

import { StaticImageData } from 'next/image';
import React, { PropsWithChildren, useCallback } from 'react';

import {
  ChildImage,
  ChildImageVariantEnum,
} from '@sola/ark-core/atoms/child-image/child-image.js';
import { Image } from '@sola/ark-core/atoms/image/image.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { CommitmentTypeEnum } from '@sola/schema/dotcom/cart/commitments.js';
import { Either } from '@sola/schema/dotcom/helpers.js';
import { DisplayPurposeProps } from '@sola/schema/dotcom/shared/display.js';
import { GiftFundCodesEnum } from '@sola/schema/dotcom/shared/gifts.js';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.js';
import { IconNames } from '@sola/schema/lib/icons/icon-names.js';

// NOTE Should this be a setting in SDG?
export const defaultIcon: IconNames = 'heart-hand';

export type WithoutText = {
  imgSrc?: string | StaticImageData;
  commitment?: GraphQLCommitment;
};
export type WithText = {
  text: string;
};

type CommitmentAvatarWrapperProps = {
  className?: string;
  purpose?: DisplayPurposeProps['variants'];
  responsive?: boolean;
  size?: 'sm' | 'lg';
  isStacked?: boolean;
};

export type CommitmentAvatarProps = Either<WithText, WithoutText> &
  CommitmentAvatarWrapperProps;

const GiftIconMap: Record<string, IconNames> = {
  [GiftFundCodesEnum.Birthday]: 'birthday-cake',
  [GiftFundCodesEnum.Child]: 'beneficiary',
  [GiftFundCodesEnum.Family]: 'family-gift',
  [GiftFundCodesEnum.Center]: 'church',
} as const;

const CommitmentAvatarWrapper: React.FC<
  PropsWithChildren<CommitmentAvatarWrapperProps>
> = ({
  children,
  className,
  purpose,
  responsive,
  size = 'sm',
  isStacked = false,
}) => {
  const purposeClass = purpose ? `CommitmentAvatar--${purpose}` : null;

  return (
    <div
      className={cn(
        'CommitmentAvatar rounded-full flex items-center justify-center relative',
        [
          purposeClass,
          className,
          {
            // eslint-disable-next-line prettier/prettier
            'CommitmentAvatar--responsive md:label-xl': responsive,
            'CommitmentAvatar--large label-xl': size === 'lg',
            'CommitmentAvatar--stacked': isStacked,
          },
        ],
      )}
    >
      {children}
    </div>
  );
};

export const CommitmentAvatar: React.FC<CommitmentAvatarProps> = ({
  commitment,
  text,
  imgSrc,
  ...wrapperProps
}) => {
  const Icon = useCallback((): null | React.ReactNode => {
    if (text) {
      return <span className="label-md label-potent">{text}</span>;
    }
    if (imgSrc) {
      return (
        <Image
          className="absolute w-full h-full"
          src={imgSrc}
          alt=""
          width={48}
          height={48}
        />
      );
    }
    if (commitment?.commitmentType === CommitmentTypeEnum.ChildSponsorship) {
      return commitment?.participant?.photoUrl ? (
        <ChildImage
          childImageUrl={commitment?.participant.photoUrl}
          alt="Participant"
          variant={ChildImageVariantEnum.Circle}
          className="CommitmentAvatar__image"
          width={48}
        />
      ) : (
        <ArkIcon iconName="beneficiary" />
      );
    }

    let iconName = commitment?.fund?.icon as IconNames;
    if (commitment?.commitmentType === CommitmentTypeEnum.GiftCommitment) {
      iconName =
        GiftIconMap[commitment?.fund?.code as GiftFundCodesEnum] ?? iconName;
    }

    return <ArkIcon iconName={iconName || defaultIcon} />;
  }, []);

  return (
    <CommitmentAvatarWrapper {...wrapperProps}>
      <Icon />
    </CommitmentAvatarWrapper>
  );
};
